import { GET_PLINKO_GAMES, GET_PLINKO_GAMES_BY_PLAYER } from '@/api/query'
import { LastGamesList } from '@/components/organisms/LastGamesList/LastGamesList'
import { Flex, SimpleGrid, Text } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { memo, useState } from 'react'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { BigNumber } from 'bignumber.js'
import { useTranslation } from 'next-i18next/pages'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { getTokenByDenom } from '@/services/token-service'
import { useSession } from '@/context/SessionProvider'

export const PlinkoLastGames = memo(() => {
  const { t } = useTranslation()
  const [onlyMine, setOnlyMine] = useState(false)
  const modal = useAppModal()
  const { account } = useSession()
  const address = account?.injectiveAddress
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())

  const allQuery = useQuery(GET_PLINKO_GAMES, {
    pollInterval: 30_000,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
    skip: onlyMine,
  })

  const myQuery = useQuery(GET_PLINKO_GAMES_BY_PLAYER, {
    pollInterval: 30_000,
    returnPartialData: true,
    fetchPolicy: 'no-cache',
    variables: { player: address || '' },
    skip: !onlyMine || !address,
  })

  const activeNodes = onlyMine ? myQuery.data?.plinkoGames?.nodes : allQuery.data?.plinkoGames?.nodes
  const activeLoading = onlyMine ? myQuery.loading : allQuery.loading
  const activeError = onlyMine ? !!myQuery.error : !!allQuery.error

  const mappedGames = activeNodes
    ?.map((game) => {
      const isUsdcToken = getTokenByDenom(game!.denom)?.currency === SupportedCurrencies.USDC
      const useFiatEquivalent = !isUsd && !isUsdcToken
      return {
        ...game!,
        bet: new TokenMoney(new BigNumber(game!.bet).multipliedBy(game!.balls), game!.denom).toCoin(),
        payout: new TokenMoney(game!.payout, game!.denom).toCoin(),
        betUsd: useFiatEquivalent && game!.betUsd != null
          ? String(new BigNumber(String(game!.betUsd)).multipliedBy(game!.balls))
          : undefined,
        payoutUsd: useFiatEquivalent && game!.payoutUsd != null
          ? String(game!.payoutUsd)
          : undefined,
      }
    })
    .reverse() || []

  return (
    <Flex h={'full'} p={{ base: 4, sm: 5 }} bg={'ninja.600'} rounded={'2xl'} flexDir={'column'}>
      <SimpleGrid
        flexShrink={0}
        gap={1}
        p={1}
        h={10}
        fontSize={'xs'}
        fontWeight={700}
        bg={'ninja.500'}
        rounded={'xl'}
        columns={address ? 2 : 1}
        mb={3}
      >
        <Flex
          onClick={() => setOnlyMine(false)}
          cursor={'pointer'}
          rounded={'lg'}
          bg={!onlyMine ? '#3D3C70' : 'transparent'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text color={!onlyMine ? 'white' : 'ninja.300'}>{t('common.last-games')}</Text>
        </Flex>
        {address && (
          <Flex
            onClick={() => setOnlyMine(true)}
            bg={onlyMine ? '#3D3C70' : 'transparent'}
            cursor={'pointer'}
            rounded={'lg'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Text color={onlyMine ? 'white' : 'ninja.300'}>{t('common.my-games')}</Text>
          </Flex>
        )}
      </SimpleGrid>
      <LastGamesList
        gameDetailsCallback={(id) => modal.openModalNew(AppModals.PlinkoGameDetails, { gameId: id })}
        error={activeError}
        loading={activeLoading}
        games={mappedGames}
        viewAllHref={onlyMine && address ? `/activity/${address}?game=plinko` : undefined}
      />
    </Flex>
  )
})
